import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { GraduationCap, MessageCircle, Calendar, ArrowRight } from "lucide-react";

const TITLE = "Agente de IA para escolas e cursos no WhatsApp | FabChat";
const DESCRIPTION =
  "Agente de IA para escolas e cursos no WhatsApp e Instagram: atendimento de matrículas, dúvidas de alunos e qualificação de contatos.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Educação", path: "/setores/educacao" },
];

export default function SetoresEducacao() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/setores/educacao" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agente de IA para educação</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Atendimento automatizado no WhatsApp e Instagram para escolas, cursos e instituições: matrículas, dúvidas e suporte a alunos.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Por que usar a FabChat em educação</h2>
            <p className="text-muted-foreground mb-4">
              Escolas e cursos recebem muitas mensagens sobre matrículas, programas, valores e suporte a alunos. Com um <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA</Link> na FabChat, é possível responder dúvidas frequentes, informar sobre turmas e disponibilidade e, quando configurado, agendar reuniões ou provas com integração a calendário. O contato vira lead no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>, com histórico para a equipe continuar o atendimento.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos que se encaixam no dia a dia</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <GraduationCap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Respostas a dúvidas sobre matrículas, cursos, horários e documentação.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Suporte a alunos: perguntas frequentes e encaminhamento para humano quando necessário.</span>
              </li>
              <li className="flex gap-3">
                <Calendar className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Agendamento com integração a Google Calendar, quando fizer sentido para o fluxo.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">CRM e funil para equipes pedagógicas</h2>
            <p className="text-muted-foreground mb-4">
              Os contatos que chegam por WhatsApp ou <Link to="/instagram/agente-ia" className="text-primary hover:underline">Instagram</Link> viram leads no CRM. Você organiza por estágios (ex.: interesse, matrícula em andamento, matriculado) e acompanha o histórico no mesmo painel.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Se quiser comparar por canal, veja <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA para WhatsApp</Link> e <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado para educação</Link>.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
